/**
 * Word List Form View
 *
 * Unified page for creating and editing word lists.
 */

import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { WordListForm } from '../components/WordListForm';
import { LocalizationsPanel } from '../components/LocalizationsPanel';
import {
  useWordList,
  useCreateWordList,
  useUpdateWordList,
  useAssignLearningGoals,
  exportWordList,
} from '../services/wordListsApi';
import { useSetLocalizationsBulk } from '../services/localizationApi';
import type { WordListCreateRequest, WordListUpdateRequest } from '../types/wordList.types';
import type { WordListLocalizationCreate } from '../types/localization.types';

export function WordListFormView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  // API hooks
  // Note: useWordList already has enabled: !!id built in
  const { data: wordList, isLoading, error } = useWordList(id ?? '');
  const createWordList = useCreateWordList();
  const updateWordList = useUpdateWordList();
  const assignLearningGoals = useAssignLearningGoals();
  const setLocalizationsBulk = useSetLocalizationsBulk();

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formLanguageCode, setFormLanguageCode] = useState('');
  const [localizations, setLocalizations] = useState<WordListLocalizationCreate[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const { state: initialDataState } = useInitialData();
  const languages = (initialDataState.data?.languages ?? []).filter(
    (lang) => lang.code !== 'zxx' && lang.code !== 'other'
  );

  // Get language from URL context (for create mode)
  const contextLanguage = searchParams.get('lang') || languages[0]?.code || 'en';

  // Track form changes for localizations panel
  const handleFormChange = (name: string, description: string, languageCode: string) => {
    setFormName(name);
    setFormDescription(description);
    setFormLanguageCode(languageCode);
  };

  const handleSubmit = async (
    data: (WordListCreateRequest | WordListUpdateRequest) & { learningGoalIds?: string[] }
  ) => {
    const { learningGoalIds, ...formData } = data;

    if (isEdit && id) {
      // Update existing word list
      updateWordList.mutate(
        { id, data: formData as WordListUpdateRequest },
        {
          onSuccess: async () => {
            const extras: string[] = [];

            // Save learning goals
            if (learningGoalIds) {
              try {
                await assignLearningGoals.mutateAsync({ id, learningGoalIds });
                extras.push(`${learningGoalIds.length} learning goal(s)`);
              } catch (err) {
                toast.error(`Failed to update learning goals: ${(err as Error).message}`);
              }
            }

            // Save localizations
            if (localizations.length > 0) {
              try {
                await setLocalizationsBulk.mutateAsync({ listId: id, localizations });
                extras.push(`${localizations.length} localization(s)`);
              } catch (err) {
                toast.error(`Failed to save localizations: ${(err as Error).message}`);
              }
            }

            if (extras.length > 0) {
              toast.success(`Word list updated with ${extras.join(' and ')}`);
            } else {
              toast.success(`Word list "${wordList?.name}" updated successfully`);
            }
            navigate('/words/lists');
          },
          onError: (err) => {
            toast.error(`Failed to update word list: ${(err as Error).message}`);
          },
        }
      );
    } else {
      // Create new word list
      createWordList.mutate(formData as WordListCreateRequest, {
        onSuccess: async (newWordList) => {
          const extras: string[] = [];

          // Assign learning goals
          if (learningGoalIds && learningGoalIds.length > 0) {
            try {
              await assignLearningGoals.mutateAsync({
                id: newWordList.id,
                learningGoalIds,
              });
              extras.push(`${learningGoalIds.length} learning goal(s)`);
            } catch (err) {
              toast.error(
                `Word list created, but failed to assign learning goals: ${(err as Error).message}`
              );
            }
          }

          // Save localizations
          if (localizations.length > 0) {
            try {
              await setLocalizationsBulk.mutateAsync({
                listId: newWordList.id,
                localizations,
              });
              extras.push(`${localizations.length} localization(s)`);
            } catch (err) {
              toast.error(
                `Word list created, but failed to save localizations: ${(err as Error).message}`
              );
            }
          }

          if (extras.length > 0) {
            toast.success(`Word list "${newWordList.name}" created with ${extras.join(' and ')}`);
          } else {
            toast.success(`Word list "${newWordList.name}" created successfully`);
          }
          navigate(`/words/lists?lang=${contextLanguage}`);
        },
        onError: (err) => {
          toast.error(`Failed to create word list: ${(err as Error).message}`);
        },
      });
    }
  };

  const handleCancel = () => {
    navigate(isEdit ? '/words/lists' : `/words/lists?lang=${contextLanguage}`);
  };

  const handleExport = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      await exportWordList(id);
      toast.success('Word list exported successfully');
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Loading state (edit mode only)
  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error state (edit mode only)
  if (isEdit && error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading word list</p>
          <p className="text-gray-600">{(error as Error).message}</p>
          <button
            onClick={() => navigate('/words/lists')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to Word Lists
          </button>
        </div>
      </div>
    );
  }

  // Not found state (edit mode only)
  if (isEdit && !wordList) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Word list not found</p>
          <button
            onClick={() => navigate('/words/lists')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Back to Word Lists
          </button>
        </div>
      </div>
    );
  }

  // Determine current language code for localizations panel
  const currentLanguageCode = isEdit ? (wordList?.languageCode ?? '') : formLanguageCode;
  const currentName = isEdit ? (wordList?.name ?? '') : formName;
  const currentDescription = isEdit ? wordList?.description : formDescription;

  const isSubmitting =
    createWordList.isPending ||
    updateWordList.isPending ||
    assignLearningGoals.isPending ||
    setLocalizationsBulk.isPending;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">{isEdit ? 'Edit Word List' : 'Create Word List'}</h1>
          {isEdit && id && (
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <WordListForm
            wordList={isEdit ? wordList : undefined}
            onSubmit={handleSubmit as (data: unknown) => void}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            isEdit={isEdit}
            contextLanguage={isEdit ? undefined : contextLanguage}
            onFormChange={handleFormChange}
          >
            {/* Localizations section inside form */}
            {currentLanguageCode && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <LocalizationsPanel
                  listId={isEdit ? id : undefined}
                  listName={currentName}
                  listDescription={currentDescription}
                  listLanguageCode={currentLanguageCode}
                  localizations={localizations}
                  onChange={setLocalizations}
                />
              </div>
            )}
          </WordListForm>
        </div>
      </div>
    </div>
  );
}
